import Vue from 'vue'
import VeeValidate from 'vee-validate'
import vuetify from '@/plugins/vuetify'
import excel from 'vue-excel-export'
import LogRocket from 'logrocket'

import App from './App.vue'
import CompleteContractEmployerKyc from './views/contract-employer/CompleteContractEmployerKyc.vue'
import Login from './Login.vue'
import Error from './Error.vue'
import LandingPage from './Landing.vue'
import InvalidPage from './InvalidUser.vue'

import router from './router'
import store from './store'
import './assets/myStyle.css'

import moment from 'moment'
import vueCurrencyInput from 'vue-currency-input'
import VueNumericInput from 'vue-numeric-input'

import { ActionTypes } from '@/utils/constants'
import MsalAuthentication from '@/msal-auth'
import AccountService from '@/services/accountService'

Vue.use(vueCurrencyInput)
Vue.use(VeeValidate)
Vue.use(VueNumericInput)
Vue.use(excel)

Vue.config.productionTip = false

Vue.filter('formatDate', function (value) {
  if (value === null || value === undefined) {
    return ''
  }

  return moment(value).format('MM-DD-YYYY')
})

Vue.filter('formatMoney', function (amount, decimalCount = 2, decimal = '.', thousands = ',') {
  try {
    decimalCount = Math.abs(decimalCount)
    decimalCount = isNaN(decimalCount) ? 2 : decimalCount

    const negativeSign = amount < 0 ? '-' : ''

    let i = parseInt(amount = Math.abs(Number(amount) || 0).toFixed(decimalCount)).toString()
    let j = (i.length > 3) ? i.length % 3 : 0

    return negativeSign +
      (j ? i.substr(0, j) + thousands : '') +
      i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
      (decimalCount ? decimal + Math.abs(amount - i).toFixed(decimalCount).slice(2) : '')
  } catch (e) {
    return ''
  }
})

let vueInstance = null

const mountComponent = (component, data) => {
  if (vueInstance) {
    vueInstance.$destroy()

    if (vueInstance.$el) {
      vueInstance.$el.innerHTML = ''
    }
  }

  vueInstance = new Vue({
    router,
    store,
    vuetify,
    data: {
      eventBus: new Vue(),
      ...(data || {})
    },
    provide() {
      return {
        eventBus: this.eventBus
      }
    },
    render: h => h(component)
  }).$mount('#app')
}

const showApp = () => {
  mountComponent(App)
}

const showLandingPage = () => {
  mountComponent(LandingPage)
}

const showLogin = () => {
  mountComponent(Login)
}

const showContractorEmployer = () => {
  mountComponent(CompleteContractEmployerKyc)
}

const showError = error => {
  mountComponent(Error, { error })
}

const showNoAccess = error => {
  mountComponent(InvalidPage, { error })
}

if (process.env.NODE_ENV === 'production') {
  LogRocket.init('ywnzvp/cmms-q7zxm')
}

function redirectToLogin() {
  sessionStorage.removeItem('api_token_redirect_started')

  if (window.location.pathname.toLowerCase() !== '/login') {
    window.history.replaceState({}, document.title, '/login')
  }

  showLogin()
}

async function loadCurrentUserAndShowApp() {
  const result = await store.dispatch(ActionTypes.GET_CURRENT_USER)

  if (result && result.status === 200) {
    const username = localStorage.getItem('newuser')

    if (username !== null && username !== undefined) {
      showContractorEmployer()
    } else {
      showApp()
    }

    return
  }

  if (result && result.status === 404) {
    showNoAccess()
    return
  }

  redirectToLogin()
}

async function bootstrapApp() {
  try {
    const hasAppToken = localStorage.getItem('user-token') !== null
    const isMsalAuthenticated = MsalAuthentication.isAuthenticated()

    /*
      Flow 1:
      External user login.
      External users have user-token in localStorage.
    */
    if (hasAppToken) {
      await loadCurrentUserAndShowApp()
      return
    }

    /*
      Flow 2:
      SSO user login.
      SSO users do NOT have user-token.
      They use MSAL access token for API calls.
    */
    if (isMsalAuthenticated) {
      const useremail = MsalAuthentication.getUserEmail()

      if (useremail) {
        localStorage.setItem('useremail', useremail)
      }

      const apiAccessToken = await MsalAuthentication.acquireToken()

      if (!apiAccessToken) {
        if (MsalAuthentication.isInteractiveSsoFlowActive()) {
          MsalAuthentication.acquireApiTokenRedirect()
          return
        }

        redirectToLogin()
        return
      }

      const signInResponse = await new AccountService().login(apiAccessToken)

      const signInData = signInResponse && signInResponse.data
        ? signInResponse.data
        : null

      const signInCode = signInData
        ? signInData.code || signInData.Code
        : null

      if (signInCode !== 202) {
        redirectToLogin()
        return
      }

      MsalAuthentication.completeSsoFlow()

      await loadCurrentUserAndShowApp()
      return
    }

    /*
      Flow 3:
      No external token and no MSAL account.
      Show login page.
    */
    redirectToLogin()
  } catch (error) {
    sessionStorage.removeItem('api_token_redirect_started')
    redirectToLogin()
  }
}

async function startApp() {
  const currentPath = window.location.pathname.toLowerCase()

  const hasAuthHash =
    window.location.hash &&
    (
      window.location.hash.indexOf('id_token') !== -1 ||
      window.location.hash.indexOf('access_token') !== -1 ||
      window.location.hash.indexOf('error') !== -1
    )

  if (
    sessionStorage.getItem('api_token_redirect_started') === 'true' &&
    localStorage.getItem('user-token') === null
  ) {
    sessionStorage.removeItem('api_token_redirect_started')
  }

  showLandingPage()

  await MsalAuthentication.initialize()

  const hasAppToken = localStorage.getItem('user-token') !== null
  const isMsalAuthenticated = MsalAuthentication.isAuthenticated()

  if (
    currentPath === '/login' &&
    !hasAuthHash &&
    !isMsalAuthenticated &&
    !hasAppToken
  ) {
    showLogin()
    return
  }

  await bootstrapApp()
}

startApp()